import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import { fr } from 'date-fns/locale';
import { setDefaultOptions } from 'date-fns';

// Active la locale française pour date‑fns (utilisée par Chart.js)
setDefaultOptions({ locale: fr });
// Force Chart.js à utiliser cette locale (adaptateur date‑fns)
if (Chart?._adapters?._date?.setLocale) {
  Chart._adapters._date.setLocale(fr);
}

// Métaux configurés
const METALS = [
  { label: 'Or',      symbol: 'XAU', color: 'gold'     },
  { label: 'Argent',  symbol: 'XAG', color: 'silver'   },
  { label: 'Platine', symbol: 'XPT', color: 'darkgray' }
];

// Conversion once troy → kilogramme (1 kg = 32,150 746 6 oz troy)
const OZ_TO_KG = 32.1507466;

const MetalYearWidgetPage = () => {
  const [selected, setSelected] = useState(METALS[0].symbol);
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  // Récupération + rendu
  const fetchAndRender = async (symbol, borderColor) => {
    // Date de fin : aujourd’hui
    const end = new Date().toISOString().slice(0, 10);

    // Date de début : 9 avril (année courante si déjà passée, sinon année‑1)
    const today = new Date();
    let startDate = new Date(today.getFullYear(), 3, 9); // 3 = avril (0‑indexé)
    if (today < startDate) {
      // On est avant le 9 avril ; on prend le 9 avril de l’année précédente
      startDate.setFullYear(startDate.getFullYear() - 1);
    }
    const start = startDate.toISOString().slice(0, 10);

    const res = await fetch(`/api/metals/history?symbol=${symbol}&start=${start}&end=${end}`);
    if (!res.ok) throw new Error(res.statusText);
    const data = await res.json();

    const labels = data.map(p => p.date);
    // Convertit le prix €/oz en €/kg
    const values = data.map(p => p.mid * OZ_TO_KG);

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          data: values,
          borderColor,
          borderWidth: 2,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 6,
          tension: 0.2
        }]
      },
      options: {
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            callbacks: {
              label: ctx =>
                new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })
                  .format(ctx.parsed.y) + ' €/kg'
            }
          }
        },
        scales: {
          x: {
            type: 'time',
            adapters: { date: { locale: fr } },
            time: { unit: 'month', displayFormats: { month: 'MMM yyyy' } },
            grid: { display: true }
          },
          y: {
            grid: { display: true },
            ticks: {
              callback: v =>
                new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })
                  .format(v) + ' €/kg'
            }
          }
        },
        hover: { mode: 'index', intersect: false }
      }
    });
  };

  useEffect(() => {
    const cfg = METALS.find(m => m.symbol === selected);
    fetchAndRender(cfg.symbol, cfg.color);
  }, [selected]);

  return (
    <div style={{
      width:  '100%',
      height: '100%',
      padding: 16,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      background: '#fff',
      borderRadius: 8,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    }}>
      {/* Onglets */}
      <div style={{ display: 'flex', marginBottom: 12 }}>
        {METALS.map(m => (
          <button
            key={m.symbol}
            onClick={() => setSelected(m.symbol)}
            style={{
              flex: 1,
              padding: '8px 0',
              background: 'none',
              border: 'none',
              borderBottom: selected === m.symbol
                ? `3px solid ${m.color}`
                : '3px solid transparent',
              fontWeight: selected === m.symbol ? 'bold' : 'normal',
              cursor: 'pointer'
            }}
          >
            {m.label}
          </button>
        ))}
      </div>
      {/* Graphique */}
      <div style={{ flex: 1, position: 'relative' }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};

export default MetalYearWidgetPage;